import { Match, Predicate, Schema } from 'effect';

import { rawUrlEncode } from '@vs-shop/domain/string/rawUrlEncode';
import type { ListingSelection } from '@vs-shop/engine/capability/productListing/listingSelection';
import type { ListingScope } from '@vs-shop/engine/capability/productListing/listingScope';
import { getWarehouseUuidByCode } from '@vs-shop/schemas/tenant/warehouses';
import { WarehouseCode } from '@vs-shop/schemas/product/filter/state/warehouse';

type QueryValue = string | number | boolean | ReadonlyArray<string>;
export type ProductListingQuery = Record<string, QueryValue>;

const isWarehouseCode = Schema.is(WarehouseCode);

/** Translates a neutral listing selection at the RCProfi API boundary. */
export const toProductListingQuery = (
  scope: ListingScope,
  selection: ListingSelection,
  withSort = true
): ProductListingQuery => {
  const query: ProductListingQuery = Match.value(scope).pipe(
    Match.tag('Taxon', ({ code }) => ({ taxon: `/api/v2/shop/taxons/${rawUrlEncode(code)}` })),
    Match.tag('Brand', ({ id }) => ({ brand: [id] })),
    Match.tag('Query', ({ text }) => ({ q: text })),
    Match.exhaustive
  );

  for (const { key, values } of selection.terms) {
    Match.value(key).pipe(
      Match.tag('Brand', () => {
        if (!Predicate.isTagged(scope, 'Brand')) query.brand = values.join(',');
      }),
      Match.tag('Taxon', () => {
        if (Predicate.isTagged(scope, 'Query')) query.taxon = values.join(',');
      }),
      Match.tag('StockLocation', () => {
        const warehouses = values.filter(isWarehouseCode).map(getWarehouseUuidByCode);
        if (warehouses.length > 0) {
          query.warehouseStock = warehouses.map((id) => `${id}:1`).join(',');
        }
      }),
      Match.tag('Attribute', ({ code }) => {
        if (values.length > 0) query[`attribute_${code}`] = values.join('|');
      }),
      Match.tag('Price', () => {}),
      Match.exhaustive
    );
  }

  for (const { key, min, max } of selection.ranges) {
    Match.value(key).pipe(
      Match.tag('Price', () => {
        if (min !== null) query['price[gte]'] = Math.floor(min);
        if (max !== null) query['price[lte]'] = Math.ceil(max);
      }),
      Match.tag('Attribute', ({ code }) => {
        if (min !== null) query[`attribute_${code}[gte]`] = String(Math.floor(min));
        if (max !== null) query[`attribute_${code}[lte]`] = String(Math.ceil(max));
      }),
      Match.tag('Brand', () => {}),
      Match.tag('Taxon', () => {}),
      Match.tag('StockLocation', () => {}),
      Match.exhaustive
    );
  }

  if (withSort) {
    Match.value(selection.sort.field).pipe(
      Match.when('none', () => {}),
      Match.when('price', () => {
        query['order[price]'] = selection.sort.direction;
      }),
      Match.when('name', () => {
        query['order[name]'] = selection.sort.direction;
      }),
      Match.when('created', () => {
        query['order[createdAt]'] = selection.sort.direction;
      }),
      Match.exhaustive
    );
  }

  return query;
};
